import { SendIcon } from '@diligentcorp/atlas-react-icons';
import { IssueStatusChip } from '@issuemanager/apps/client/components';
import {
  AppHeaderPrimaryButtonProps,
  useHeaderStore,
} from '@issuemanager/apps/client/contexts';
import { getIssueHeaderTitle } from '@issuemanager/apps/client/utils';
import { useSaveIssue } from '@issuemanager/client/features/issues';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { IssueStatusEnum } from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIssueStatusInfo } from './use-issue-status-info';
import { AssignIssueFormValues } from './use-manage-issue-form';
import useResetFormData from './use-reset-form-data';
import { useIssueOutletContext } from './useIssueOutletContext';

// TODO: Delete this workflow action lookup dictionary once extended workflow is merged
const workflowActionLookup = {
  extended: {
    savingAnd: 'issue:actions.savingAndApprovingFromInReview',
    saveAnd: 'issue:actions.saveAndApproveFromInReview',
    ongoing: 'issue:actions.approvingFromInReview',
    base: 'issue:actions.approveFromInReview',
  },
  standard: {
    savingAnd: 'issue:actions.savingAndAssigning',
    saveAnd: 'issue:actions.saveAndAssign',
    ongoing: 'issue:actions.assigning',
    base: 'issue:actions.assign',
  },
} as const;

// TODO: Delete this workflow action lookup dictionary once extended workflow is merged
const submitActionLookup = {
  extended: {
    ongoing: 'issue:actions.submitting',
    base: 'issue:actions.submit',
    success: 'issue:actions.submitSuccess',
  },
  standard: {
    ongoing: 'issue:actions.movingToOpen',
    base: 'issue:actions.moveToOpen',
    success: 'issue:manage.moveToOpen.success',
  },
} as const;

// TODO: Delete this workflow action lookup dictionary once extended workflow is merged
const declineActionLookup = {
  extended: {
    ongoing: 'issue:actions.decliningFromInReview',
    base: 'issue:actions.declineFromInReview',
    success: 'issue:actions.declineFromInReviewSuccess',
  },
  standard: {
    ongoing: 'issue:actions.movingToDraft',
    base: 'issue:actions.moveToDraft',
    success: 'issue:manage.moveToDraft.success',
  },
} as const;

type Params = {
  manageIssueForm: UseFormReturn<
    AssignIssueFormValues,
    any,
    AssignIssueFormValues
  >;
  assignIssueIsLoading: boolean;
  initiateWorkflowIsLoading: boolean;
  manageIssueHandleSubmit: (submitCallback?: () => Promise<void>) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isLoading: boolean;
  activateDeclinePanel: () => void;
  activateApprovePanel: () => void;
  handleSave: () => void;
  saveIssue: ReturnType<typeof useSaveIssue>;
  handleSubmitToInReview?: () => void;
  handleMoveToDraft?: () => void;
};

export const useHeaderButtons = ({
  manageIssueForm,
  assignIssueIsLoading,
  initiateWorkflowIsLoading,
  manageIssueHandleSubmit,
  isEditing,
  setIsEditing,
  isLoading,
  activateDeclinePanel,
  activateApprovePanel,
  handleSave,
  saveIssue,
  handleSubmitToInReview,
  handleMoveToDraft,
}: Params): void => {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const {
    code,
    issue,
    userRoles: issueUserRoles,
    canDiscardIssue,
    onDiscardIssue,
  } = useIssueOutletContext();
  const { setHeaderProps } = useHeaderStore();
  const isDraftIssuesEnabled = useFeatureFlags('draftIssues');
  const isExtendedWorkflowEnabled = useFeatureFlags('issueExtendedWorkflow');
  const isIssueTypesExtraFieldsEnabled = useFeatureFlags(
    'issueTypesExtraFields',
  );

  const {
    isInitiated,
    isClosedOrDiscarded,
    isNotYetAssigned,
    isReadyToApprove,
  } = useIssueStatusInfo({
    status: issue.data?.status,
    isInitiated: issue.data?.isInitiated,
    approvalStatus: issue.data?.approvalStatus,
  });

  const { resetForm } = useResetFormData({
    manageIssueForm,
  });

  const handleCancel = useEventCallback(() => {
    setIsEditing(false);
    resetForm();
  });

  const disabledBtns = isLoading;

  const assignButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    const workflowAction = isExtendedWorkflowEnabled
      ? workflowActionLookup.extended
      : workflowActionLookup.standard;

    let assignButtonLabel: string;
    if (isEditing) {
      assignButtonLabel = assignIssueIsLoading
        ? t(workflowAction.savingAnd)
        : t(workflowAction.saveAnd);
    } else {
      assignButtonLabel = assignIssueIsLoading
        ? t(workflowAction.ongoing)
        : t(workflowAction.base);
    }

    return {
      id: 'assign-button',
      variant: 'contained',
      disabled: disabledBtns,
      loading: assignIssueIsLoading,
      onClick: () => manageIssueHandleSubmit(),
      children: assignButtonLabel,
      'data-testid': 'assign-button',
    };
  }, [
    isEditing,
    disabledBtns,
    assignIssueIsLoading,
    manageIssueHandleSubmit,
    isExtendedWorkflowEnabled,
    t,
  ]);

  const initiateButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    const getInitiateButtonLabel = (): string => {
      const canInitiate = isEditing && !isLoading;
      if (initiateWorkflowIsLoading) {
        return canInitiate
          ? t('issue:actions.savingAndInitiating')
          : t('issue:actions.initiating');
      }
      return canInitiate
        ? t('issue:actions.saveAndInitiate')
        : t('issue:actions.initiate');
    };

    return {
      id: 'initiate-button',
      variant: 'contained',
      disabled: disabledBtns,
      loading: initiateWorkflowIsLoading,
      startIcon: <SendIcon />,
      onClick: () => manageIssueHandleSubmit(),
      children: getInitiateButtonLabel(),
      'data-testid': 'initiate-button',
    };
  }, [
    isLoading,
    isEditing,
    initiateWorkflowIsLoading,
    disabledBtns,
    manageIssueHandleSubmit,
  ]);

  const cancelButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'cancel-button',
      variant: 'text',
      onClick: handleCancel,
      children: t('issue:actions.cancel'),
      'data-testid': 'cancel-button',
      disabled: disabledBtns,
    }),
    [disabledBtns, handleCancel],
  );

  const saveButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'save-button',
      variant: 'outlined',
      disabled: disabledBtns,
      onClick: handleSave,
      loading: saveIssue.isPending,
      children: saveIssue.isPending
        ? t('issue:actions.saving')
        : t('issue:actions.save'),
      'data-testid': 'save-button',
    }),
    [disabledBtns, handleSave, saveIssue.isPending],
  );

  const editButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      id: 'edit-button',
      variant: 'outlined',
      disabled: disabledBtns,
      onClick: () => setIsEditing(true),
      children: t('issue:actions.edit'),
      'data-testid': 'edit-button',
    }),
    [disabledBtns, setIsEditing],
  );

  const discardButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.discard'),
      variant: 'text',
      'data-testid': 'discard-button',
      onClick: onDiscardIssue,
      disabled: disabledBtns,
    }),
    [disabledBtns, onDiscardIssue],
  );

  const approveButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.approve'),
      variant: 'contained',
      'data-testid': 'approve-button',
      onClick: activateApprovePanel,
      disabled: disabledBtns,
    }),
    [disabledBtns],
  );

  const declineButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      children: t('issue:actions.decline'),
      variant: 'outlined',
      'data-testid': 'decline-button',
      onClick: activateDeclinePanel,
      disabled: disabledBtns,
    }),
    [disabledBtns],
  );

  const moveToOpenButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    const submitAction = isExtendedWorkflowEnabled
      ? submitActionLookup.extended
      : submitActionLookup.standard;

    return {
      children: disabledBtns ? t(submitAction.ongoing) : t(submitAction.base),
      variant: 'contained',
      'data-testid': 'move-to-open-button',
      onClick: handleSubmitToInReview,
      disabled: disabledBtns,
      loading: disabledBtns,
    };
  }, [disabledBtns, handleSubmitToInReview, t, isExtendedWorkflowEnabled]);

  const moveToDraftButtonProps: AppHeaderPrimaryButtonProps = useMemo(() => {
    const declineAction = isExtendedWorkflowEnabled
      ? declineActionLookup.extended
      : declineActionLookup.standard;

    return {
      children: disabledBtns ? t(declineAction.ongoing) : t(declineAction.base),
      variant: 'outlined',
      'data-testid': 'move-to-draft-button',
      onClick: handleMoveToDraft,
      disabled: disabledBtns,
      loading: disabledBtns,
    };
  }, [disabledBtns, handleMoveToDraft, t, isExtendedWorkflowEnabled]);

  const isDraftIssue =
    isDraftIssuesEnabled && issue.data?.status === IssueStatusEnum.DRAFT;
  const isIssueOpen = issue.data?.status === IssueStatusEnum.OPEN;
  const isIssueCreator = Boolean(issueUserRoles.data?.isCreator);
  const isIssueTypeManager = Boolean(issueUserRoles.data?.isIssueTypeManager);
  const isIssueManager = Boolean(issueUserRoles.data?.isManager);
  const isIssueOwner = Boolean(issueUserRoles.data?.isOwner);
  const isApprover = Boolean(issueUserRoles.data?.isApprover);
  const isNotYetInitiated =
    issue.data?.status === IssueStatusEnum.IN_PROGRESS &&
    !issue.data?.isInitiated;

  const canManageDraft =
    isIssueCreator ||
    (isIssueTypesExtraFieldsEnabled && (isIssueTypeManager || isIssueManager));

  const confirmButton: AppHeaderPrimaryButtonProps | null = useMemo(() => {
    if (isNotYetAssigned || assignIssueIsLoading) {
      if (isIssueTypeManager) {
        return assignButtonProps;
      }
    } else if (isNotYetInitiated) {
      if (isIssueTypeManager || isIssueManager || isIssueOwner) {
        return initiateButtonProps;
      }
    }

    return null;
  }, [
    assignButtonProps,
    initiateButtonProps,
    isNotYetAssigned,
    isNotYetInitiated,
    isIssueTypeManager,
    isIssueManager,
    isIssueOwner,
    assignIssueIsLoading,
  ]);

  const editingButtonsProps: AppHeaderPrimaryButtonProps[] = useMemo(() => {
    const getButtonsForEditableIssue = (): AppHeaderPrimaryButtonProps[] =>
      isEditing ? [cancelButtonProps, saveButtonProps] : [editButtonProps];

    if (isDraftIssue) {
      return canManageDraft ? getButtonsForEditableIssue() : [];
    }

    const isAuthorizedToEdit =
      isIssueTypeManager || isIssueManager || isIssueOwner;

    const isInEditableState =
      isInitiated || (!isInitiated && !isClosedOrDiscarded);

    const hasEditPermission = isAuthorizedToEdit && isInEditableState;

    return hasEditPermission ? getButtonsForEditableIssue() : [];
  }, [
    cancelButtonProps,
    editButtonProps,
    isIssueTypeManager,
    isClosedOrDiscarded,
    isEditing,
    isInitiated,
    isIssueManager,
    isIssueOwner,
    saveButtonProps,
    isDraftIssue,
    canManageDraft,
  ]);

  useEffect(() => {
    const issueData = issue.data;
    if (!issueData || issueUserRoles.isFetching) return;

    let buttons: AppHeaderPrimaryButtonProps[] = [];

    if (isDraftIssue) {
      buttons = [
        ...(canDiscardIssue && !isEditing && canManageDraft
          ? [discardButtonProps]
          : []),
        ...editingButtonsProps,
        ...(moveToOpenButtonProps && !isEditing && isIssueCreator
          ? [moveToOpenButtonProps]
          : []),
      ];
    } else {
      buttons = [
        ...(isReadyToApprove && isApprover && !isEditing
          ? [declineButtonProps]
          : []),
        ...(isReadyToApprove && isApprover && !isEditing
          ? [approveButtonProps]
          : []),
        ...(canDiscardIssue && !isEditing ? [discardButtonProps] : []),
        ...editingButtonsProps,
        ...(isDraftIssuesEnabled &&
        isIssueOpen &&
        isIssueTypeManager &&
        !isEditing
          ? [moveToDraftButtonProps]
          : []),
        ...(confirmButton ? [confirmButton] : []),
      ];
    }

    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [{ url: '/', title: t('common:appName') }],
        title: getIssueHeaderTitle({
          code,
          title: issueData.title,
        }),
      },
      chip: issueData.status ? (
        <IssueStatusChip
          status={issueData.status}
          isInitiated={issueData.isInitiated}
        />
      ) : undefined,
      buttons,
    });
  }, [
    canDiscardIssue,
    code,
    isEditing,
    isReadyToApprove,
    issue.dataUpdatedAt,
    issueUserRoles.isFetching,
    discardButtonProps,
    declineButtonProps,
    confirmButton,
    isApprover,
    approveButtonProps,
    editingButtonsProps,
    isDraftIssue,
    canManageDraft,
    moveToOpenButtonProps,
    isIssueOpen,
    moveToDraftButtonProps,
    isIssueTypeManager,
    isIssueCreator,
  ]);
};
